import { cn } from "@/libs/utils";
import React, {
  cloneElement,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Text } from "src/common/Text";
import { useTrans } from "src/utils/useTrans";
import { Breadcrumb } from "../components/Breadcrumb";
import { useRouter } from "next/router";
import SupportBannerImgSection from "@/v2-components/pages/support/shared/support-banner-img-section";
import {
  DownloadsEnum,
  resolveDownloadsType,
} from "@/v2-components/pages/support/types/support-downloads-type";
import { SupportDownloadsSlideSection } from "@/v2-components/pages/support/components/downloads/support-downloads-slide-section";

const bannerImg = "/images/support/contactus/support-contact-us-banner.jpg";
const mobileBannerImg =
  "/images/support/contactus/support-contact-us-mobile-banner.jpg";
export function DownloadsPageLayout({ children }: any) {
  const { t } = useTrans(["support"]);
  const router = useRouter();
  const { query } = router;
  const [id, setId] = useState<DownloadsEnum>(() =>
    resolveDownloadsType(query.type),
  );

  const OPTIONS = useMemo(
    () => [
      { id: DownloadsEnum.Manual, name: t("Manual") },
      { id: DownloadsEnum.BhapticsPlayer, name: t("bHaptics Player") },
    ],
    [t],
  );

  const option = OPTIONS.find((i) => i.id === id);
  const labels = useMemo(
    () => [t("Downloads"), option?.name ?? ""].filter(Boolean),
    [t, option],
  );

  // URL 보정도 같은 판정 함수를 쓴다 — 종전엔 여기서 따로 Manual 로 되돌렸다(판정이 세 벌).
  useEffect(() => {
    const { type } = query;
    const target = resolveDownloadsType(type);
    if (type === target) {
      setId(target);
    } else {
      router.replace(`/support/downloads?type=${target}`);
    }
  }, [query, router]);

  const handleOnClickSideBar = (newId: DownloadsEnum) => {
    router.push(`/support/downloads?type=${newId}`);
    setId(newId);
  };

  return (
    <Fragment>
      <SupportDownloadsSlideSection />
      <SupportBannerImgSection
        bannerImg={bannerImg}
        mobileBannerImg={mobileBannerImg}
        bannerTitle={"Downloads"}
      />
      <div className="md:h-[80px]" />
      <div className="w-full px-5">
        <div className="mx-auto max-w-screen-mdl gap-x-5 md:flex md:gap-x-10 lg:gap-x-6">
          <div className="bg-gray_9 relative flex h-[52px] items-center justify-between md:hidden">
            <div className="flex grow pb-3">
              <Breadcrumb labels={labels} />
            </div>
          </div>

          <Sidebar id={id} onClick={handleOnClickSideBar} options={OPTIONS} />
          <Content id={id}>{children}</Content>
        </div>
      </div>
      <div className="h-[50px] md:h-[80px]" />
    </Fragment>
  );
}

const Sidebar = ({
  id,
  onClick,
  options,
}: {
  id: DownloadsEnum;
  onClick: (id: DownloadsEnum) => void;
  options: { id: DownloadsEnum; name: string }[];
}) => {
  const { t } = useTrans(["support"]);
  return (
    <div className="hidden min-w-[320px] flex-col gap-y-8 md:flex">
      <h2 className="text-4xl font-400"> {t("Downloads")} </h2>
      <div className="flex flex-col">
        {options.map((option, index) => {
          const selected = option.id === id;
          return (
            <button
              onClick={() => onClick(option.id)}
              key={index}
              className={cn("hover:bg-gray_9 h-9 py-2 pl-7 text-left", {
                "font-600": selected,
              })}
            >
              <Text text={t(option.name)} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Content = ({ id, children }) => (
  <div className="grow">{cloneElement(children, { id })}</div>
);
